import type { Face } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import HalfEdgeMesh from '../HalfEdgeMesh';

export interface RemoveFaceSetResult {
  removedCount: number;
  remainingFaceCount: number;
}

/**
 * Removes faces by rebuilding geometry from the kept face set (`excludedBufferGeometry`
 * + `setNewGeometry`). Produces a clean mesh with no orphan border vertices.
 *
 * Prefer this over repeated vendor `halfEdges.removeFace()` for bulk deletion.
 * Invalidates existing `Vertex` / `Face` references on this mesh.
 */
export function removeFaceSet(mesh: HalfEdgeMesh, facesToRemove: Iterable<Face>): RemoveFaceSetResult {
  const toRemove = new Set<Face>();
  for (const face of facesToRemove) {
    if (face.halfedge?.face === face) {
      toRemove.add(face);
    }
  }

  const totalFaces = mesh.halfEdges.faces.length;
  if (toRemove.size === 0) {
    return { removedCount: 0, remainingFaceCount: totalFaces };
  }
  if (toRemove.size >= totalFaces) {
    console.warn('removeFaceSet: refusing to remove all faces');
    return { removedCount: 0, remainingFaceCount: totalFaces };
  }

  const sub = mesh.halfEdges.excludedBufferGeometry(toRemove);
  if (sub.oldFaces.length === 0) {
    console.warn('removeFaceSet: no faces would remain after removal');
    return { removedCount: 0, remainingFaceCount: totalFaces };
  }

  mesh.geometry.dispose();
  mesh.setNewGeometry(sub.newGeometry);

  return {
    removedCount: toRemove.size,
    remainingFaceCount: sub.oldFaces.length,
  };
}
